import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { dashboardApi, timeEntriesApi, projectsApi, reportsApi, boardApi } from "../lib/api";
import { UserAvatar } from "../components/UserAvatar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Clock, 
  Snowflake,
  CloudRain,
  Copy,
  Calendar, 
  CheckSquare, 
  FolderKanban, 
  Users, 
  TrendingUp, 
  ArrowRight,
  DollarSign,
  FileText,
  Briefcase,
  User,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  MessageSquare,
  Send,
  Trash2,
} from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, eachDayOfInterval, isWeekend } from "date-fns";
import { sv } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const { user, canManage, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [notInvoicedData, setNotInvoicedData] = useState({ hours: 0, amount: 0 });
  const [profitData, setProfitData] = useState(null);
  const [missingDays, setMissingDays] = useState([]);
  const [weekDayHours, setWeekDayHours] = useState([]);
  const [missingReports, setMissingReports] = useState(null);
  
  // Modal states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState("");
  const [detailsEntries, setDetailsEntries] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [projectProfitExpanded, setProjectProfitExpanded] = useState(false);
  const [missingExpanded, setMissingExpanded] = useState(false);

  // Board/Chat state
  const [boardMessages, setBoardMessages] = useState([]);
  const [boardText, setBoardText] = useState("");
  const [boardSending, setBoardSending] = useState(false);

  // Weather
  const [weather, setWeather] = useState(null);
  const [tomorrow, setTomorrow] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    // Eskilstuna coordinates
    fetch("https://api.open-meteo.com/v1/forecast?latitude=59.37&longitude=16.51&current=temperature_2m,weather_code,wind_speed_10m&daily=temperature_2m_min,precipitation_sum,precipitation_probability_max&forecast_days=2&timezone=Europe/Stockholm")
      .then(r => r.json())
      .then(d => {
        if (d.current) setWeather(d.current);
        if (d.daily?.time?.length > 1) {
          setTomorrow({
            min: d.daily.temperature_2m_min[1],
            rain: d.daily.precipitation_sum[1] || 0,
            prob: d.daily.precipitation_probability_max?.[1] || 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleCopyLast = async () => {
    setCopying(true);
    try {
      const res = await timeEntriesApi.copyLast();
      const { count, hours, source_date } = res.data;
      toast.success(`${count} ${count === 1 ? "post" : "poster"} (${hours}h) kopierade från ${format(new Date(source_date), "EEE d/M", { locale: sv })}`);
      loadStats();
      checkMissingDays();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte kopiera");
    } finally {
      setCopying(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Kunde inte ladda statistik", error);
      toast.error("Kunde inte ladda statistik");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error("Kunde inte ladda projekt", error);
      toast.error("Kunde inte ladda projekt");
    }
  }, []);

  const loadNotInvoiced = useCallback(async () => {
    try {
      const today = new Date();
      const response = await reportsApi.getSummary({
        start_date: "2020-01-01",
        end_date: format(today, "yyyy-MM-dd"),
        status: "godkänd",
        invoiced: false,
      });
      const hours = response.data.total_hours || 0;
      const amount = response.data.total_amount || 0;
      setNotInvoicedData({ hours, amount });
    } catch (error) {
      console.error("Kunde inte ladda faktureringsstatus", error);
      toast.error("Kunde inte ladda faktureringsstatus");
    }
  }, []);

  const loadProfit = useCallback(async () => {
    try {
      const res = await dashboardApi.getProfit(24);
      setProfitData(res.data);
    } catch (error) {
      // Not admin or no data
    }
  }, []);

  const loadMissingReports = useCallback(async () => {
    try {
      const res = await dashboardApi.getMissingReports();
      setMissingReports(res.data);
    } catch {
      // Not admin/manager
    }
  }, []);

  // Check which workdays this week are missing time entries
  const checkMissingDays = useCallback(async () => {
    try {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const checkEnd = subDays(today, 0);
      const response = await timeEntriesApi.getAll({
        start_date: format(weekStart, "yyyy-MM-dd"),
        end_date: format(checkEnd, "yyyy-MM-dd"),
      });
      const reportedDates = new Set(response.data.map(e => e.date));
      const days = eachDayOfInterval({ start: weekStart, end: subDays(today, 1) });
      const missing = days.filter(d => !isWeekend(d) && !reportedDates.has(format(d, "yyyy-MM-dd")));
      setMissingDays(missing);

      // Build weekly hours breakdown (Mon-Fri)
      const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek(today, { weekStartsOn: 1 }) });
      const dayMap = {};
      response.data.forEach(e => {
        dayMap[e.date] = (dayMap[e.date] || 0) + e.hours;
      });
      const breakdown = weekDays.filter(d => !isWeekend(d)).map(d => ({
        date: format(d, "yyyy-MM-dd"),
        label: format(d, "EEE", { locale: sv }),
        hours: dayMap[format(d, "yyyy-MM-dd")] || 0,
        isToday: format(d, "yyyy-MM-dd") === format(today, "yyyy-MM-dd"),
        isPast: d < today && format(d, "yyyy-MM-dd") !== format(today, "yyyy-MM-dd"),
      }));
      setWeekDayHours(breakdown);
    } catch (error) {
      // Skip
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadProjects();
    checkMissingDays();
    if (canManage) {
      loadNotInvoiced();
      loadMissingReports();
    }
    if (isAdmin) {
      loadProfit();
    }
    // Load board messages
    boardApi.getMessages(30).then(res => setBoardMessages(res.data)).catch(() => {});
  }, [isAdmin, canManage, loadStats, loadProjects, loadNotInvoiced, loadProfit, checkMissingDays, loadMissingReports]);

  const showWeekDetails = async () => {
    setDetailsLoading(true);
    setDetailsTitle("Denna vecka");
    setDetailsOpen(true);
    
    try {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      
      const response = await timeEntriesApi.getAll({
        start_date: format(weekStart, "yyyy-MM-dd"),
        end_date: format(weekEnd, "yyyy-MM-dd"),
      });
      setDetailsEntries(response.data);
    } catch (error) {
      toast.error("Kunde inte ladda veckodata");
    } finally {
      setDetailsLoading(false);
    }
  };

  const showMonthDetails = async () => {
    setDetailsLoading(true);
    setDetailsTitle("Denna månad");
    setDetailsOpen(true);
    
    try {
      const today = new Date();
      const monthStart = startOfMonth(today);
      const monthEnd = endOfMonth(today);
      
      const response = await timeEntriesApi.getAll({
        start_date: format(monthStart, "yyyy-MM-dd"),
        end_date: format(monthEnd, "yyyy-MM-dd"),
      });
      setDetailsEntries(response.data);
    } catch (error) {
      toast.error("Kunde inte ladda månadsdata");
    } finally {
      setDetailsLoading(false);
    }
  };

  const showPendingEntries = async () => {
    setPendingLoading(true);
    setPendingModalOpen(true);
    
    try {
      const response = await timeEntriesApi.getAll({ status: "väntande" });
      setPendingEntries(response.data);
    } catch (error) {
      toast.error("Kunde inte ladda väntande poster");
    } finally {
      setPendingLoading(false);
    }
  };

  const showProjectDetails = (project) => {
    setSelectedProject(project);
    setProjectModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      väntande: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      godkänd: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      avvisad: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return <Badge className={styles[status] || ""} variant="outline">{status}</Badge>;
  };

  // Group entries by user - memoized
  const groupedByUser = useMemo(() => detailsEntries.reduce((acc, entry) => {
    const userName = entry.user_name || "Okänd";
    if (!acc[userName]) {
      acc[userName] = { entries: [], totalHours: 0, totalAmount: 0 };
    }
    acc[userName].entries.push(entry);
    acc[userName].totalHours += entry.hours;
    acc[userName].totalAmount += (entry.hours * (entry.hourly_rate || 0));
    return acc;
  }, {}), [detailsEntries]);

  // Totals from grouped entries - memoized
  const detailsTotals = useMemo(() => {
    const vals = Object.values(groupedByUser);
    return {
      hours: vals.reduce((s, u) => s + u.totalHours, 0),
      amount: vals.reduce((s, u) => s + u.totalAmount, 0),
    };
  }, [groupedByUser]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-10 w-64 skeleton" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="card-surface p-5 space-y-3">
              <div className="h-5 w-5 skeleton rounded-xl" />
              <div className="h-8 w-20 skeleton" />
              <div className="h-3 w-24 skeleton" />
            </div>
          ))}
        </div>
        <div className="card-surface p-6 space-y-4">
          <div className="h-5 w-40 skeleton" />
          {[1,2,3].map(i => <div key={i} className="h-16 skeleton" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 slide-up relative" data-testid="dashboard">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {(() => { const h = new Date().getHours(); return h < 5 ? "God natt" : h < 10 ? "God morgon" : h < 17 ? "God eftermiddag" : "God kväll"; })()}, {user?.name?.split(" ")[0]}<span className="text-[#6dbf4a]">.</span>
          </h1>
          <p className="text-white/25 text-sm mt-1">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: sv })}
            {weather && (
              <span className="ml-2 text-white/40">
                · {Math.round(weather.temperature_2m)}°
                {(() => {
                  const c = weather.weather_code;
                  if (c === 0) return " Sol";
                  if (c <= 3) return " Molnigt";
                  if (c <= 48) return " Dimma";
                  if (c <= 67) return " Regn";
                  if (c <= 77) return " Sno";
                  if (c <= 99) return " Aska";
                  return "";
                })()}
              </span>
            )}
          </p>
        </div>
        {isAdmin && stats?.pending_approvals > 0 && (
          <button className="px-4 py-2 text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-xl cursor-pointer hover:bg-amber-500/20 transition-all duration-200 backdrop-blur-sm"
            onClick={showPendingEntries} data-testid="pending-approvals-badge">
            {stats.pending_approvals} att godkänna
          </button>
        )}
      </div>

      {/* Weather warning for tomorrow */}
      {tomorrow && (tomorrow.min < 0 || tomorrow.rain >= 1 || tomorrow.prob >= 60) && (
        <div className="flex items-center gap-3 px-4 py-3 bg-sky-500/5 border border-sky-500/20 rounded-xl" data-testid="weather-warning-banner">
          {tomorrow.min < 0 ? <Snowflake className="w-4 h-4 text-sky-300 flex-shrink-0" /> : <CloudRain className="w-4 h-4 text-sky-300 flex-shrink-0" />}
          <span className="text-xs text-sky-200 flex-1">
            I morgon: {[
              tomorrow.min < 0 && `minusgrader (ner till ${Math.round(tomorrow.min)}°)`,
              (tomorrow.rain >= 1 || tomorrow.prob >= 60) && `regn (${tomorrow.rain.toFixed(1)} mm, ${tomorrow.prob}% risk)`,
            ].filter(Boolean).join(" och ")} — planera utomhusjobben
          </span>
        </div>
      )}

      {/* Quick report */}
      <button onClick={handleCopyLast} disabled={copying}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#6dbf4a]/[0.06] border border-[#6dbf4a]/20 hover:bg-[#6dbf4a]/10 active:scale-[0.99] transition-[background-color,transform] text-left disabled:opacity-50"
        data-testid="quick-copy-last-btn">
        <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/15 flex items-center justify-center flex-shrink-0">
          <Copy className="w-4 h-4 text-[#6dbf4a]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">{copying ? "Kopierar..." : "Samma som senast"}</p>
          <p className="text-[11px] text-white/35">Rapportera idag med samma projekt och timmar som din senaste arbetsdag</p>
        </div>
      </button>

      {/* Missing days reminder */}
      {missingDays.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-testid="missing-days-banner">
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <span className="text-xs text-amber-300">
              Du saknar tid för {missingDays.map(d => format(d, "EEE d/M", { locale: sv })).join(", ")}
            </span>
          </div>
          <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/20 text-xs rounded-xl h-7"
            onClick={() => navigate(`/time-entry?date=${format(missingDays[0], 'yyyy-MM-dd')}`)}>
            Rapportera
          </Button>
        </div>
      )}

      {/* Main Stats — unified card design */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
        <div className="card-surface-hover rounded-2xl p-5 cursor-pointer" onClick={showWeekDetails} data-testid="stat-week">
          <div className="flex items-center justify-between mb-4">
            <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#6dbf4a]" />
            </div>
            <span className="section-label">Vecka</span>
          </div>
          <p className="stat-number text-3xl text-white">{stats?.week_hours?.toFixed(1) || "0"}<span className="text-base text-slate-500 ml-0.5">h</span></p>
        </div>

        <div className="card-surface-hover rounded-2xl p-5 cursor-pointer" onClick={showMonthDetails} data-testid="stat-month">
          <div className="flex items-center justify-between mb-4">
            <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/10 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-[#6dbf4a]" />
            </div>
            <span className="section-label">Manad</span>
          </div>
          <p className="stat-number text-3xl text-white">{stats?.month_hours?.toFixed(1) || "0"}<span className="text-base text-slate-500 ml-0.5">h</span></p>
        </div>

        {canManage ? (
          <div className="card-surface-hover rounded-2xl p-5 cursor-pointer" onClick={showPendingEntries} data-testid="stat-pending">
            <div className="flex items-center justify-between mb-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <CheckSquare className="w-4 h-4 text-amber-400" />
              </div>
              <span className="section-label">Att granska</span>
            </div>
            <p className="stat-number text-3xl text-white">{stats?.pending_approvals || 0}</p>
          </div>
        ) : (
          <div className="card-surface rounded-2xl p-5" data-testid="stat-projects">
            <div className="flex items-center justify-between mb-4">
              <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/10 flex items-center justify-center">
                <FolderKanban className="w-4 h-4 text-[#6dbf4a]" />
              </div>
              <span className="section-label">Projekt</span>
            </div>
            <p className="stat-number text-3xl text-white">{stats?.active_projects || 0}</p>
          </div>
        )}

        {isAdmin && (
          <div className="card-surface-hover rounded-2xl p-5 cursor-pointer" onClick={() => navigate('/reports')} data-testid="stat-invoice">
            <div className="flex items-center justify-between mb-4">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-red-400" />
              </div>
              <span className="section-label">Att fakturera</span>
            </div>
            <p className="stat-number text-3xl text-white">{notInvoicedData.hours.toFixed(1)}<span className="text-base text-slate-500 ml-0.5">h</span></p>
          </div>
        )}
      </div>

      {/* Missing Reports - Clickable expandable bar (Admin/Manager) */}
      {canManage && missingReports && (missingReports.missing_today.length > 0 || missingReports.low_week_hours.length > 0) && (
        <div className="rounded-xl bg-amber-500/5 border border-amber-500/15 overflow-hidden" data-testid="missing-reports-bar">
          <button onClick={() => setMissingExpanded(!missingExpanded)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-500/10 transition-colors">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <div className="flex-1 flex items-center gap-4 text-sm text-left">
              {missingReports.missing_today.length > 0 && (
                <span className="text-amber-300/80">
                  <span className="font-semibold text-amber-400">{missingReports.missing_today.length}</span> ej rapporterat idag
                </span>
              )}
              {missingReports.low_week_hours.length > 0 && (
                <span className="text-amber-300/80">
                  <span className="font-semibold text-amber-400">{missingReports.low_week_hours.length}</span> låga timmar
                </span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-amber-400/50 transition-transform duration-200 ${missingExpanded ? 'rotate-180' : ''}`} />
          </button>
          {missingExpanded && (
            <div className="px-4 pb-3 space-y-2 border-t border-amber-500/10 pt-2">
              {missingReports.missing_today.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-400/50 mb-1">Ej rapporterat {missingReports.date}</p>
                  {missingReports.missing_today.map(u => (
                    <button key={u.id} onClick={() => navigate(`/time-entry?date=${missingReports.date}&user=${u.id}`)}
                      className="flex items-center gap-2 w-full text-left py-1.5 px-2 rounded-lg hover:bg-amber-500/10 transition-colors group">
                      <div className="w-6 h-6 rounded-lg bg-amber-500/15 flex items-center justify-center text-[10px] text-amber-400 font-bold">{u.name.charAt(0)}</div>
                      <span className="text-sm text-white flex-1">{u.name}</span>
                      <span className="text-xs text-amber-400/50 group-hover:text-amber-400">Rapportera →</span>
                    </button>
                  ))}
                </div>
              )}
              {missingReports.low_week_hours.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-400/50 mb-1">Låga timmar denna vecka</p>
                  {missingReports.low_week_hours.map(u => (
                    <div key={u.id} className="flex items-center gap-2 py-1.5 px-2">
                      <div className="w-6 h-6 rounded-lg bg-amber-500/15 flex items-center justify-center text-[10px] text-amber-400 font-bold">{u.name.charAt(0)}</div>
                      <span className="text-sm text-white flex-1">{u.name}</span>
                      <span className="text-xs text-amber-400 font-mono">{u.hours}h / {u.expected}h</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Employee Weekly Overview + Quick Actions */}
      {!canManage && weekDayHours.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Weekly Hour Bars */}
          <Card className="card-surface lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">Veckans timmar</h3>
              <div className="flex items-end gap-2 h-32">
                {weekDayHours.map((day) => {
                  const maxH = Math.max(...weekDayHours.map(d => d.hours), 8);
                  const pct = maxH > 0 ? (day.hours / maxH) * 100 : 0;
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1.5">
                      <span className={`text-xs font-mono ${day.hours > 0 ? "text-white" : "text-white/25"}`}>
                        {day.hours > 0 ? day.hours.toFixed(1) : "–"}
                      </span>
                      <div className="w-full bg-white/[0.06] rounded-md overflow-hidden" style={{ height: '80px' }}>
                        <div
                          className={`w-full rounded-md transition-all duration-500 ${
                            day.isToday ? "bg-[#6dbf4a]" : day.hours > 0 ? "bg-[#6dbf4a]/60" : "bg-white/[0.03]"
                          }`}
                          style={{ height: `${Math.max(pct, 4)}%`, marginTop: 'auto', position: 'relative', top: `${100 - Math.max(pct, 4)}%` }}
                        />
                      </div>
                      <span className={`text-[11px] capitalize ${day.isToday ? "text-[#6dbf4a] font-semibold" : "text-white/40"}`}>
                        {day.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                <span className="text-xs text-white/40">Totalt denna vecka</span>
                <span className="stat-number text-lg text-white">{stats?.week_hours?.toFixed(1) || "0"}h</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="card-surface">
            <CardContent className="p-5 flex flex-col gap-2">
              <h3 className="section-label mb-2">Snabbatgarder</h3>
              {[
                { to: '/time-entry', icon: Clock, label: 'Rapportera tid', testId: 'quick-time-entry' },
                { to: '/schedule', icon: Calendar, label: 'Planering', testId: 'quick-schedule' },
                { to: '/documents', icon: FileText, label: 'Dokument', testId: 'quick-documents' },
              ].map(a => (
                <button key={a.to} onClick={() => navigate(a.to)}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-[#6dbf4a]/30 transition-all text-left"
                  data-testid={a.testId}>
                  <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/10 flex items-center justify-center">
                    <a.icon className="w-4 h-4 text-[#6dbf4a]" />
                  </div>
                  <span className="text-sm font-medium text-white">{a.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-600 ml-auto" />
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Anslagstavla */}
      <Card className="card-surface" data-testid="board">
        <CardContent className="pt-5 pb-4">
          <h3 className="section-label mb-4 flex items-center gap-2">
            <MessageSquare className="w-3.5 h-3.5 text-[#6dbf4a]" /> Anslagstavla
          </h3>

          {/* Post message */}
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!boardText.trim() || boardSending) return;
            setBoardSending(true);
            try {
              const res = await boardApi.post(boardText.trim());
              setBoardMessages(prev => [res.data, ...prev]);
              setBoardText("");
            } catch { toast.error("Kunde inte skicka"); }
            finally { setBoardSending(false); }
          }} className="flex gap-2 mb-4" data-testid="board-form">
            <Input value={boardText} onChange={(e) => setBoardText(e.target.value)}
              placeholder="Skriv ett meddelande..."
              className="flex-1 input-dark"
              data-testid="board-input" />
            <Button type="submit" size="icon" disabled={!boardText.trim() || boardSending}
              className="bg-[#6dbf4a] hover:bg-[#7ed458] active:scale-[0.98] h-10 w-10 flex-shrink-0 transition-all"
              data-testid="board-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>

          {/* Messages */}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {boardMessages.length > 0 ? boardMessages.map(msg => (
              <div key={msg.id} className="flex gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.02] transition-colors group"
                data-testid={`board-msg-${msg.id}`}>
                <UserAvatar userId={msg.user_id} name={msg.user_name} className="w-8 h-8 mt-0.5" testId={`board-avatar-${msg.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{msg.user_name}</span>
                    {msg.created_at && (
                      <span className="text-[11px] text-slate-600">
                        {(() => {
                          const d = new Date(msg.created_at);
                          const now = new Date();
                          const diffMin = Math.floor((now - d) / 60000);
                          if (diffMin < 1) return "just nu";
                          if (diffMin < 60) return `${diffMin} min`;
                          const diffH = Math.floor(diffMin / 60);
                          if (diffH < 24) return `${diffH}h`;
                          return format(d, "d MMM HH:mm", { locale: sv });
                        })()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-0.5 whitespace-pre-wrap break-words">{msg.text}</p>
                </div>
                {(msg.user_id === user?.id || isAdmin) && (
                  <button onClick={async () => {
                    try {
                      await boardApi.delete(msg.id);
                      setBoardMessages(prev => prev.filter(m => m.id !== msg.id));
                    } catch { toast.error("Kunde inte ta bort"); }
                  }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-transparent hover:text-red-400 hover:bg-red-500/10 group-hover:text-slate-700 transition-all flex-shrink-0"
                    data-testid={`delete-msg-${msg.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            )) : (
              <p className="text-center text-slate-600 text-sm py-8">Inga meddelanden annu</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin: Compact financial summary - link to Rapporter */}
      {isAdmin && profitData && profitData.total_revenue > 0 && (
        <button onClick={() => navigate('/reports')} className="w-full flex items-center gap-6 p-4 rounded-2xl hover:bg-white/[0.03] transition-all duration-200 group" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="financial-summary">
          <div className="flex-1 flex items-center gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/25">Intäkter</p>
              <p className="stat-number text-xl text-white">{(profitData.total_revenue / 1000).toFixed(1)}<span className="text-sm text-white/30 ml-0.5">k</span></p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/25">Kostnad</p>
              <p className="stat-number text-xl text-red-400">{(profitData.total_cost / 1000).toFixed(1)}<span className="text-sm text-red-400/50 ml-0.5">k</span></p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/25">Vinst</p>
              <p className={`stat-number text-xl ${profitData.total_profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                {(profitData.total_profit / 1000).toFixed(1)}<span className="text-sm opacity-50 ml-0.5">k</span>
                <span className="text-sm ml-2 opacity-60">{profitData.total_revenue > 0 ? `${((profitData.total_profit / profitData.total_revenue) * 100).toFixed(1)}%` : "0%"}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/20 group-hover:text-[#6dbf4a] transition-colors">
            <span>Se rapporter</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </button>
      )}

      {/* Admin Quick Navigation */}
      {canManage && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-children">
          {[
            { to: '/time-entry', icon: Clock, label: 'Rapportera tid', sub: 'Ny tidspost', testId: 'quick-time-entry' },
            { to: '/schedule', icon: Calendar, label: 'Planering', sub: 'Schema & tilldelning', testId: 'quick-schedule' },
            { to: '/approvals', icon: CheckSquare, label: 'Godkann', sub: 'Granska tidsposter', testId: 'quick-approvals' },
            { to: '/projects', icon: FolderKanban, label: 'Projekt', sub: 'Kunder & projekt', testId: 'quick-projects' },
          ].map(a => (
            <button key={a.to} onClick={() => navigate(a.to)}
              className="flex items-center gap-3 p-4 rounded-2xl card-surface-hover text-left"
              data-testid={a.testId}>
              <div className="w-9 h-9 rounded-lg bg-[#6dbf4a]/10 flex items-center justify-center flex-shrink-0">
                <a.icon className="w-4 h-4 text-[#6dbf4a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{a.label}</p>
                <p className="text-[11px] text-slate-600">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Week/Month Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">{detailsTitle}</DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
            </div>
          ) : detailsEntries.length > 0 ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Totalt timmar</p>
                  <p className="text-2xl font-bold text-white">
                    {detailsTotals.hours.toFixed(1)}h
                  </p>
                </div>
                {isAdmin && (
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <p className="text-sm text-white/40">Totalt belopp</p>
                    <p className="text-2xl font-bold text-[#6dbf4a]">
                      {detailsTotals.amount.toLocaleString("sv-SE")} kr
                    </p>
                  </div>
                )}
              </div>

              {/* Per User */}
              {canManage && Object.keys(groupedByUser).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Per anställd</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(groupedByUser).map(([userName, data]) => (
                      <div key={userName} className="bg-[#080c12] rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-[#6dbf4a]" />
                            </div>
                            <span className="font-medium text-white">{userName}</span>
                          </div>
                          <span className="text-[#6dbf4a] font-semibold">{data.totalHours.toFixed(1)}h</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Table */}
              <Table>
                <TableHeader>
                  <TableRow className="border-white/[0.06]">
                    <TableHead className="text-white/40">Datum</TableHead>
                    {canManage && <TableHead className="text-white/40">Anställd</TableHead>}
                    <TableHead className="text-white/40">Projekt</TableHead>
                    <TableHead className="text-white/40 text-right">Timmar</TableHead>
                    <TableHead className="text-white/40">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailsEntries.map((entry) => (
                    <TableRow key={entry.id} className="border-white/[0.06]">
                      <TableCell className="text-white">{entry.date}</TableCell>
                      {canManage && <TableCell className="text-white/40">{entry.user_name}</TableCell>}
                      <TableCell className="text-white">{entry.project_name}</TableCell>
                      <TableCell className="text-white text-right">{entry.hours}h</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-white/40">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Inga tidsposter för denna period</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending Entries Modal */}
      <Dialog open={pendingModalOpen} onOpenChange={setPendingModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-amber-400" />
              Väntar på godkännande
            </DialogTitle>
          </DialogHeader>
          
          {pendingLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
            </div>
          ) : pendingEntries.length > 0 ? (
            <div className="space-y-4">
              <p className="text-white/40">{pendingEntries.length} poster väntar på godkännande</p>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/[0.06]">
                    <TableHead className="text-white/40">Datum</TableHead>
                    <TableHead className="text-white/40">Anställd</TableHead>
                    <TableHead className="text-white/40">Projekt</TableHead>
                    <TableHead className="text-white/40">Aktivitet</TableHead>
                    <TableHead className="text-white/40 text-right">Timmar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingEntries.map((entry) => (
                    <TableRow key={entry.id} className="border-white/[0.06]">
                      <TableCell className="text-white">{entry.date}</TableCell>
                      <TableCell className="text-white/40">{entry.user_name}</TableCell>
                      <TableCell className="text-white">{entry.project_name}</TableCell>
                      <TableCell className="text-white/40">{entry.activity_name}</TableCell>
                      <TableCell className="text-white text-right">{entry.hours}h</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button 
                onClick={() => { setPendingModalOpen(false); navigate('/approvals'); }}
                className="w-full bg-[#6dbf4a] hover:bg-[#5da83d]"
              >
                Gå till godkännanden
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-white/40">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Inga poster väntar på godkännande</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Projects List Modal */}
      <Dialog open={projectsModalOpen} onOpenChange={setProjectsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <FolderKanban className="w-6 h-6 text-[#6dbf4a]" />
              Alla aktiva projekt
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {projects.map((project) => (
              <div 
                key={project.id}
                className="bg-[#080c12] rounded-xl p-4 hover:bg-[#0a0e14] cursor-pointer transition-colors"
                onClick={() => { setProjectsModalOpen(false); showProjectDetails(project); }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">{project.name}</h3>
                    <p className="text-sm text-white/40">{project.client_name}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Project Details Modal */}
      <Dialog open={projectModalOpen} onOpenChange={setProjectModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#6dbf4a]" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">{selectedProject?.name}</DialogTitle>
                <p className="text-white/40">{selectedProject?.client_name}</p>
              </div>
            </div>
          </DialogHeader>
          
          {selectedProject && (
            <div className="space-y-6">
              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4">
                {isAdmin && selectedProject.hourly_rate > 0 && (
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/40 mb-1">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">Timpris</span>
                    </div>
                    <p className="text-xl font-bold text-[#6dbf4a]">{selectedProject.hourly_rate} kr/h</p>
                  </div>
                )}
                {selectedProject.address && (
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/40 mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">Adress</span>
                    </div>
                    <p className="text-white">{selectedProject.address}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedProject.description && (
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40 mb-2">Beskrivning</p>
                  <p className="text-white">{selectedProject.description}</p>
                </div>
              )}

              {/* Contact Persons */}
              {selectedProject.contact_persons?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#6dbf4a]" />
                    Kontaktpersoner
                  </h3>
                  <div className="space-y-3">
                    {selectedProject.contact_persons.map((contact, idx) => (
                      <div key={contact.name + contact.phone + idx} className="bg-[#080c12] rounded-xl p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white">{contact.name}</p>
                            {contact.role && <p className="text-sm text-white/40">{contact.role}</p>}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-3">
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-[#6dbf4a] hover:underline">
                              <Phone className="w-4 h-4" />
                              {contact.phone}
                            </a>
                          )}
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-[#6dbf4a] hover:underline">
                              <Mail className="w-4 h-4" />
                              {contact.email}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedProject.notes && (
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40 mb-2">Anteckningar</p>
                  <p className="text-white whitespace-pre-wrap">{selectedProject.notes}</p>
                </div>
              )}

              <Button 
                onClick={() => { setProjectModalOpen(false); navigate('/projects'); }}
                className="w-full bg-[#6dbf4a] hover:bg-[#5da83d]"
              >
                Gå till projekt
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
